import Big from 'big.js';
import {API_URLS} from '@px/const/urls';
import {contractUtil} from '@px/helper/contract';
import {numUtil} from '@px/helper/num';
import {DEFAULT_USD_PRICE_SCALE} from '@px/const/data-config';
import {http} from '@px/helper/http';

export const configApi = {
  getProductList() {
    return http.get(API_URLS.CONFIG_PRODUCTS_PLUS, {
      formatter(data) {
        let {
          currencies,
          products,
          riskLimits,
          leverages,
          leveragesV2 = [],
          perpProductsV2 = [],
          riskLimitsV2 = [],
          collaterals = [],
          marginRiskLevels = null,
          tradeLevels,
          leverageMargins = [],
          perpProductsPilot = [],
        } = data;
        leverages = leverages || [];
        collaterals = collaterals || [];

        const currenciesData = convertCurrencies(currencies);

        const contracts: ContractPair[] = [...products.filter((o) => o.type === 'Perpetual'), ...perpProductsV2, ...perpProductsPilot];
        const riskLimitMerged = [...riskLimits, ...convertRiskLimitV2(riskLimitsV2)];
        const contractData = convertContracts(contracts, currenciesData, riskLimitMerged);
        const riskLimitsData = convertRiskLimits(riskLimitMerged, contracts);

        const spots: SpotPair[] = products.filter((o) => o.type === 'Spot');
        const spotsData = convertSpots(spots, currenciesData);

        const leveragesData = removeRepeatData([...convertLeverages(leverages), ...convertLeverageV2(leveragesV2)], 'initialMarginEr');
        return {
          currencies: currenciesData,
          contracts: contractData,
          spots: spotsData,
          riskLimits: riskLimitsData,
          leverages: leveragesData,
          riskLimitsV2,
          collaterals,
          marginRiskLevels,
          tradeLevels,
          leverageMargins,
        };
      },
    });
  },
  getPromotions(query) {
    return http.get(API_URLS.PROMOTIONS, {query});
  },
  getFiatConfig(query = {}) {
    return http.get(API_URLS.PUBLIC_MISC, query);
  },
  getCoinBasic() {
    return http.get(API_URLS.COIN_BASIC_LIST, {});
  },
  getCoinInfoList(query) {
    return http.get(API_URLS.COIN_INFO_LIST, {query});
  },
  getPrivateCoin() {
    return http.get(API_URLS.PRIVACY_COIN, {});
  },
  getCategory(query) {
    return http.get(API_URLS.CATEGORY_LIST, {
      query,
      formatter({total, rows: items}) {
        const rows = items.map((raw) => {
          const {coins, ...items} = raw;
          return {...items, coins: coins.map((o) => o.replace(' ', ''))};
        });
        return {total, rows};
      },
    });
  },
  getConversionPairs(query) {
    return http.get(API_URLS.CONVERSION_SUPPORTED_CURRENCIES, {query});
  },
  getCoinDetailList(query) {
    return http.get(API_URLS.COIN_DETAIL_LIST, {query});
  },
};

function convertCurrencies(currencies) {
  return currencies.map((item: CurrencyEntity) => {
    item.symbol = item.currency;
    item.valueFactor = Math.pow(10, item.valueScale);
    return item;
  });
}

function convertContracts(contracts, currencies, riskLimits) {
  return contracts.map((contract: ContractPair) => {
    const {settleCurrency, quoteCurrency, contractUnderlyingAssets, type} = contract;
    const isDoubleUnderlyingAssets = contractUnderlyingAssets.indexOf(' ') > -1 && numUtil.startWithNumber(contractUnderlyingAssets);
    let underlyingAssets = contractUnderlyingAssets;
    if (isDoubleUnderlyingAssets) {
      const tmpStr = contractUnderlyingAssets.split(' ');
      underlyingAssets = tmpStr[1];
    }
    const contractUnderlyingAssetsV2 = contractUnderlyingAssets.replaceAll(' ', '');
    const baseCurrency = settleCurrency === quoteCurrency ? underlyingAssets : settleCurrency;
    let settle = currencies.find((o) => o.currency === settleCurrency);
    if (!settle) {
      console.warn('not found config for ' + settleCurrency + ' settle currency in currencies list');
      settle = initCurrency(settleCurrency);
    }
    const {valueScale: scale} = settle;
    const base: CurrencyEntity = currencies.find((o) => o.currency === baseCurrency);
    const {steps} = riskLimits.find((o) => o.symbol === contract.symbol) || {}; // 特殊情况下币种未配置riskLimits信息
    const quote: CurrencyEntity = currencies.find((o) => o.currency === quoteCurrency);
    contract.contractSide = settleCurrency === quoteCurrency ? 1 : -1;
    contract.baseCurrencyName = base ? base.name : baseCurrency;
    contract.quoteCurrencyDisplay = quote ? quote.displayCurrency : quoteCurrency;
    contract.baseCurrency = isDoubleUnderlyingAssets ? contractUnderlyingAssetsV2 : baseCurrency;
    contract.ratioFactor = Math.pow(10, contract.ratioScale);
    contract.ratioPrecision = 2;
    contract.valuePrecision = settleCurrency === 'USD' ? 2 : 4;
    contract.riskLimitSteps = steps;
    // TODO: confirm
    const amountUnit = contract.contractSide > 0 ? baseCurrency : quoteCurrency;
    const amountUnitV2 = contract.contractSide > 0 ? contractUnderlyingAssets : quoteCurrency;
    contract.pairName = settleCurrency === quoteCurrency ? contractUnderlyingAssetsV2 + settle.displayCurrency : settleCurrency + quoteCurrency;
    contract.symbolV2 = contract.pairName;
    const isSupportHedge = contractUtil.isSupportHedgeMode(type);
    contract.isSupportHedge = isSupportHedge;
    //  TODO from config
    contract.qtyPrecision = isSupportHedge ? contract.qtyPrecision : 0;
    contract.contPrecision = isSupportHedge ? contract.qtyPrecision : 0;
    contract.settleValuePrecision = isSupportHedge ? contract.qtyPrecision : 8;
    contract.settleCurrencyDisplay = settle.displayCurrency;
    contract.qtyScale = isSupportHedge ? 0 : 8;
    contract.qtyFactor = isSupportHedge ? 1 : 1e8;
    contract.contTickSizeEv = isSupportHedge ? Number(contract.qtyStepSize) : 1e8;
    contract.settleTickSizeEv = isSupportHedge ? Number(contract.qtyStepSize) : 1;
    contract.status = genContractStatus(contract);
    contract.contractSizePrecision = genContractSizePrecision(contract);
    contract.priceScale = isSupportHedge ? 0 : DEFAULT_USD_PRICE_SCALE;
    contract.priceFactor = isSupportHedge ? 1 : 1e4;
    contract.tickSizeEp = contract.tickSize * (isSupportHedge ? 1 : 1e4);
    const valueScale = isSupportHedge ? 0 : scale;
    contract.valueScale = valueScale;
    contract.valueFactor = Math.pow(10, valueScale);
    contract.contractSize = isSupportHedge ? 1 : contract.contractSize;
    contract.minPriceEp = isSupportHedge ? Number(contract.minPriceRp) : contract.minPriceEp;
    contract.maxPriceEp = isSupportHedge ? Number(contract.maxPriceRp) : contract.maxPriceEp;
    contract.contAmount = isDoubleUnderlyingAssets ? amountUnitV2 : String(contract.contractSize) + amountUnit;
    contract.tickSizeEv = (settleCurrency === 'USD' ? 0.01 : 0.0001) * contract.valueFactor;
    contract.maxOrderQty = isSupportHedge ? Number(contract.maxOrderQtyRq) : contract.maxOrderQty;
    contract.tipOrderQty = isSupportHedge ? Number(contract.tipOrderQtyRq) : contract.tipOrderQty;
    contract.qtyStepSize = Number(contract.qtyStepSize || 0);
    contract.originType = contract.type;
    contract.urlSymbol = contract.contractSide > 0 ? `${contractUnderlyingAssetsV2}-${settle.displayCurrency}` : `${settleCurrency}-${quoteCurrency}`;
    if (contractUtil.isPilotContractV1(contract.type)) {
      contract.type = 'PerpetualV2';
    }
    delete contract.description;
    return contract;
  });
}

function genContractSizePrecision(contract: ContractPair) {
  if (contract.isSupportHedge) {
    return contract.qtyPrecision;
  }
  const {contractSize} = contract;
  if (contractSize && contractSize.toString().includes('.')) {
    return contractSize.toString().split('.')[1].length;
  }
  return 0;
}

function convertSpots(spots, currencies): SpotPair[] {
  return spots.map((spot): SpotPair => {
    let base: CurrencyEntity = currencies.find((o) => o.currency === spot.baseCurrency);
    let quote: CurrencyEntity = currencies.find((o) => o.currency === spot.quoteCurrency);
    if (!base) {
      console.warn('not found config for ' + spot.baseCurrency + ' currency in currencies list');
      base = initCurrency(spot.baseCurrency);
    }
    if (!quote) {
      console.warn('not found config for ' + spot.quoteCurrency + ' currency in currencies list');
      quote = initCurrency(spot.quoteCurrency);
    }
    spot.name = base.name;
    spot.quoteName = quote.name;
    spot.priceScale = base.valueScale;
    spot.displayBaseCurrency = base.displayCurrency;
    spot.pairName = base.symbol + quote.symbol;
    spot.indexSymbol = '.' + spot.baseCurrency;
    spot.baseQtyScale = base.valueScale;
    spot.quoteQtyScale = quote.valueScale;
    spot.baseQtyFactor = Math.pow(10, base.valueScale);
    spot.quoteQtyFactor = Math.pow(10, quote.valueScale);
    spot.baseDeltaSizeEv = spot.baseTickSizeEv;
    spot.baseDeltaSize = spot.baseTickSizeEv / 1e8;
    spot.quoteDeltaSizeEv = 100000000;
    spot.quoteDeltaSize = 1;
    spot.quoteQtyTickSizeEv = Number(Big(10).pow(Number(Big(spot.quoteQtyScale).minus(spot.quoteQtyPrecision))));
    spot.quoteQtyTickSize = Number(Big(10).pow(-spot.quoteQtyPrecision));
    spot.quoteTickSize = spot.quoteTickSizeEv / 1e8;
    spot.baseQtyTickSizeEv = spot.baseTickSizeEv;
    spot.baseQtyTickSize = spot.baseTickSizeEv / 1e8;
    spot.status = genSpotStatus(spot);
    spot.urlSymbol = `${base.symbol}-${quote.symbol}`;
    delete spot.description;
    return spot;
  });
}

function convertRiskLimits(riskLimits, contracts) {
  return riskLimits.map((item) => {
    const {symbol, riskLimits, steps} = item;
    const contract = contracts.find((contract) => contract.symbol === symbol);
    if (!contract) {
      const riskLimitsData = riskLimits.map((riskLimit) => {
        riskLimit.riskLimitEv = 0;
        return riskLimit;
      });
      return {symbol, steps, riskLimits: riskLimitsData};
    }
    const riskLimitsData = riskLimits.map((riskLimit) => {
      riskLimit.riskLimitEv = riskLimit.limit * contract.valueFactor;
      return riskLimit;
    });

    return {symbol, steps, riskLimits: riskLimitsData};
  });
}

function convertLeverages(leverages) {
  return leverages.map((o) => {
    const {initialMargin, initialMarginEr, options} = o;
    const optionsData = options.map((l) => ({
      leverageEr: l * 1e8,
      leverage: l,
    }));
    return {initialMargin, initialMarginEr, options: optionsData};
  });
}

function convertRiskLimitV2(riskLimits) {
  return riskLimits.map((item) => {
    const {symbol, riskLimits, steps} = item;
    const riskLimitsData = riskLimits.map((riskLimit) => {
      const {limit, initialMarginRr, maintenanceMarginRr} = riskLimit;
      const riskLimitEv = Number(limit);
      const initialMarginEr = Number(initialMarginRr);
      const initialMargin = Big(initialMarginRr).times(100) + '%';
      const maintenanceMarginEr = Number(maintenanceMarginRr);
      const maintenanceMargin = Big(maintenanceMarginRr).times(100) + '%';
      return {limit, riskLimitEv, initialMarginEr, maintenanceMarginEr, initialMargin, maintenanceMargin};
    });

    return {symbol, steps, riskLimits: riskLimitsData};
  });
}

function convertLeverageV2(leverages) {
  return leverages.map((item) => {
    const {initialMarginRr, options} = item;
    const factor = 1;
    const optionsData = options.map((l) => ({
      leverageEr: Number(Big(l).times(factor)),
      leverage: l,
    }));
    const initialMarginEr = Number(Big(initialMarginRr).times(factor));
    return {initialMargin: numUtil.floor(Big(initialMarginRr).times(100), 1) + '%', initialMarginEr, options: optionsData};
  });
}

function genContractStatus(item): ContractTradePairStatus {
  const [displayTime, listTime, alertTime, delistTime, hideTime] = item?.timeline || [0, 0, 0, 0, 0];
  const currentTime = Date.now();
  if (alertTime === 0 && delistTime === 0 && hideTime === 0) {
    if (currentTime < displayTime) {
      return 'Init';
    }
    if (currentTime < listTime) {
      return 'PreList';
    }
    return 'Listed';
  }
  if (currentTime < displayTime) {
    return 'Init';
  }
  if (currentTime < listTime) {
    return 'PreList';
  }
  if (currentTime < alertTime) {
    return 'Listed';
  }
  if (currentTime < delistTime) {
    return 'Suspend';
  }
  if (currentTime < hideTime) {
    return 'Delisted';
  }
  return 'Hide';
}

function genSpotStatus(item): SpotTradePairStatus {
  const currentTime = Date.now();
  const [displayTime, listTime, , delistTime, hideTime] = item?.timeline || [0, 0, 0, 0, 0];
  if (delistTime === 0 && hideTime === 0) {
    if (currentTime < displayTime) {
      return 'Init';
    }
    if (currentTime < listTime) {
      return 'PreList';
    }
    return 'Listed';
  }
  if (currentTime < displayTime) {
    return 'Init';
  }
  if (currentTime < listTime) {
    return 'PreList';
  }
  if (currentTime < delistTime) {
    return 'Listed';
  }
  if (currentTime < hideTime) {
    return 'Delisted';
  }
  return 'Hide';
}

function initCurrency(currency): CurrencyEntity {
  return {
    code: 0,
    currency: currency,
    displayCurrency: currency,
    inAssetsDisplay: 1,
    maxValueEv: 5000000000000000000,
    minValueEv: 1,
    name: currency,
    needAddrTag: 0,
    qtyPrecision: 8,
    status: 'Listed',
    symbol: currency,
    tickSizeEv: 1,
    valueFactor: 100000000,
    valuePrecision: 8,
    valueScale: 8,
  };
}

function removeRepeatData(arr, key) {
  const res = new Map();
  return arr.filter((item) => !res.has(item[key]) && res.set(item[key], 1));
}
